import React from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCcw, Home, Bug, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorFallbackProps {
  error: Error;
  errorInfo: React.ErrorInfo | null;
  eventId: string | null;
  resetError: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  eventId: string | null;
  isDetailsOpen: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      isDetailsOpen: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔴 Error caught by boundary:', error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Send to Sentry with enhanced context
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setTag('error_boundary', 'true');
      scope.setContext('componentStack', {
        componentStack: errorInfo.componentStack || '',
      });
      scope.setContext('errorInfo', {
        digest: errorInfo.digest || '',
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack || '',
      });
      
      // Add user context if available (from Redux or other state management)
      const userInfo = this.getUserInfo();
      if (userInfo) {
        scope.setUser(userInfo);
      }

      const eventId = Sentry.captureException(error);
      this.setState({ eventId, errorInfo });
    });
  }

  getUserInfo = () => {
    // Get user info from your state management (Redux, Context, etc.)
    // This is a placeholder - implement based on your auth setup
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        const user = JSON.parse(userString);
        return {
          id: user.id,
          email: user.email,
          username: user.username,
        };
      }
    } catch (error) {
      console.error('Failed to get user info:', error);
    }
    return null;
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ 
        eventId: this.state.eventId,
        title: 'It looks like we\'re having issues.',
        subtitle: 'Our team has been notified, but click submit to help us fix it faster.',
        subtitle2: 'Tell us what happened below.',
      });
    }
  };

  handleReload = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      isDetailsOpen: false,
    });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      isDetailsOpen: false,
    });
  };

  copyErrorToClipboard = () => {
    const { error, errorInfo } = this.state;
    const errorText = `
Error: ${error?.name} - ${error?.message}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}

Event ID: ${this.state.eventId || 'N/A'}
Timestamp: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      console.log('Error details copied to clipboard');
      // You can add a toast notification here
    });
  };

  toggleDetails = () => {
    this.setState((prevState) => ({
      isDetailsOpen: !prevState.isDetailsOpen,
    }));
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error!}
            errorInfo={this.state.errorInfo}
            eventId={this.state.eventId}
            resetError={this.handleReset}
          />
        );
      }

      // Default enhanced error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
          <Card className="max-w-2xl w-full shadow-lg">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-10 w-10 text-destructive" />
              </div>
              <CardTitle className="text-3xl font-bold">
                Oops! Something went wrong
              </CardTitle>
              <CardDescription className="text-base">
                We apologize for the inconvenience. Our development team has been automatically notified and will investigate this issue.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Error ID Alert */}
              {this.state.eventId && (
                <Alert>
                  <Bug className="h-4 w-4" />
                  <AlertDescription>
                    <span className="font-semibold">Error ID:</span>{' '}
                    <code className="bg-muted px-2 py-1 rounded text-sm">
                      {this.state.eventId}
                    </code>
                  </AlertDescription>
                </Alert>
              )}

              {/* Error Details Collapsible */}
              {this.props.showDetails !== false && this.state.error && (
                <Collapsible
                  open={this.state.isDetailsOpen}
                  onOpenChange={this.toggleDetails}
                  className="border rounded-lg"
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors">
                    <span className="font-medium text-sm">Technical Details</span>
                    {this.state.isDetailsOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-4 pt-0 space-y-3">
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-muted-foreground">Error Type:</p>
                      <code className="block bg-muted p-3 rounded text-sm overflow-x-auto">
                        {this.state.error.name}
                      </code>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-muted-foreground">Error Message:</p>
                      <code className="block bg-muted p-3 rounded text-sm overflow-x-auto">
                        {this.state.error.message}
                      </code>
                    </div>
                    {this.state.error.stack && (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-muted-foreground">Stack Trace:</p>
                        <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-40">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={this.copyErrorToClipboard}
                      className="w-full"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Error Details
                    </Button>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Helpful suggestions */}
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <p className="font-semibold text-sm">What you can try:</p>
                <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                  <li>Refresh the page to try again</li>
                  <li>Clear your browser cache and cookies</li>
                  <li>Go back to the homepage</li>
                  <li>Contact support if the problem persists</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col sm:flex-row gap-3">
              <Button onClick={this.handleReload} className="flex-1" size="lg">
                <RefreshCcw className="h-4 w-4 mr-2" />
                Reload Page
              </Button>
              <Button onClick={this.handleGoHome} variant="outline" className="flex-1" size="lg">
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              {this.state.eventId && (
                <Button
                  onClick={this.handleReportFeedback}
                  variant="secondary"
                  className="flex-1"
                  size="lg"
                >
                  <Bug className="h-4 w-4 mr-2" />
                  Report Issue
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
